<i18n src="./translations"></i18n>

<template>
  <div class="pdp-tab-car-types">
    <PdpVehicleNotification
      v-if="shouldShowCompatibilityPrompt"
      class="notification"
      gtm-class-name="gtm--pdp-car-types-vehicle-notification"
      :click-tracking="notificationClickTracking('section')"
      :vehicle-compatibility="vehicleCompatibility"
      :is-car-selected="isCarSelected"
      :related-items-link="relatedItemsLink"
    />

    <ExpandableTableSection
      ref="section"
      class="section"
      :trigger="trigger"
      :table="table"
      :preview-table="previewTable"
      :preview="preview"
      :expanded="expanded"
      @tableLoaded="onTableLoaded"
      @tableIsEmpty="$emit('empty', 'carTypes')"
    >
      <template #trigger="{ show, showDialog }">
        <button
          v-if="show"
          type="button"
          class="trigger"
          data-ssel-click="pdp_car_types_show_all"
          :data-ssel-label="trigger.title"
          :data-ssel-context="triggerTrackingContext"
          @click="showDialog"
        >
          {{ trigger.title }}
        </button>
      </template>

      <template #preview-before-table>
        <div
          v-if="isMatchOnTop.preview"
          class="match-info match-header"
          data-testid="matching-car-info"
        >
          <Icon class="icon" name="check" width="16" height="16" />
          <span class="label">{{
            $t('pdp-tab-car-types.car-match-label')
          }}</span>
        </div>
      </template>

      <template #dialog-before-table>
        <PdpVehicleNotification
          v-if="shouldShowModalCompatibilityPrompt"
          class="modal-notification"
          gtm-class-name="gtm--pdp-car-types-vehicle-notification"
          :click-tracking="notificationClickTracking('dialog')"
          :vehicle-compatibility="vehicleCompatibility"
          :is-car-selected="isCarSelected"
          :related-items-link="relatedItemsLink"
        />
        <div
          v-if="isMatchOnTop.expanded"
          class="match-info match-header"
          data-testid="matching-car-info"
        >
          <Icon class="icon" name="check" width="16" height="16" />
          <span class="label">{{
            $t('pdp-tab-car-types.car-match-label')
          }}</span>
        </div>
      </template>

      <template #model="{ entity }">
        <p>
          <b class="brand">{{ carModel(entity.model).model }}</b>
          <span class="type">{{ carModel(entity.model).specs }}</span>
        </p>
      </template>
    </ExpandableTableSection>
  </div>
</template>

<script>
import ExpandableTableSection from 'Components/02-molecules/expandable-table-section/ExpandableTableSection';
import PdpVehicleNotification from 'Components/02-molecules/pdp-vehicle-notification/PdpVehicleNotification';
import globalMixin from 'Libs/mixins/globalMixin';
import Icon from 'Src/components/00-generated/Icon.vue';
import { axios } from 'Services/AjaxService';
import { mapState } from 'vuex';

export default {
  name: 'PdpTabCarTypes',
  components: { ExpandableTableSection, PdpVehicleNotification, Icon },
  mixins: [globalMixin],
  props: {
    productId: { type: String, required: true },
    vehicleCompatibility: { type: String, default: 'NOT_SELECTED' },
    isCarSelected: { type: Boolean, default: false },
    relatedItemsLink: { type: String, default: '' },
  },
  data() {
    return {
      carTypesCount: null,
      carTypesPreviewCount: 4,
      // whether the selected car was sorted to the top of each table, so the match label can sit above it
      isMatchOnTop: { preview: false, expanded: false },
    };
  },
  computed: {
    ...mapState('core', {
      ktypnr: ({ placeholders }) => {
        const hasVehicle =
          placeholders?.carSelection?.data?.vehicle !== undefined;
        const vehicle = hasVehicle && placeholders.carSelection.data.vehicle;
        return vehicle ? vehicle.ktypnr : null;
      },
    }),
    ...mapState('carSelection', {
      isCarSelectorDialogActive: ({ isDialogActive }) => isDialogActive,
    }),
    ajaxUrl() {
      if (!this.productId) return '';
      return this.interpolateUrl(
        this.frontFacingPath('search.ajax.product.car.data'),
        { productId: this.productId }
      );
    },
    trigger() {
      return {
        show:
          this.carTypesCount > (this.preview.hidden ? 0 : this.previewLimit),
        title: this.$t('pdp-tab-car-types.show-all-car-types-count', {
          '%count%': this.carTypesCount,
        }),
        modifiers: ['secondary', 'slim'],
      };
    },
    previewLimit() {
      return this.vehicleCompatibility === 'SELECTED_CAR_OK'
        ? 1
        : this.carTypesPreviewCount;
    },
    preview() {
      const hasMatchingCar = this.vehicleCompatibility === 'SELECTED_CAR_OK';

      return {
        limit: this.previewLimit,
        hidden: !hasMatchingCar,
      };
    },
    expanded() {
      return {
        title: this.$t('pdp-tab-car-types.suitable-cars'),
        limit: 10,
        modifier: 'pdp-car-types',
      };
    },
    shouldShowCompatibilityPrompt() {
      return (
        !this.isCarSelected ||
        this.vehicleCompatibility === 'SELECTED_CAR_NOT_OK'
      );
    },
    shouldShowModalCompatibilityPrompt() {
      return this.shouldShowCompatibilityPrompt;
    },
    triggerTrackingContext() {
      return JSON.stringify({
        product_id: this.productId,
        car_types_count: String(this.carTypesCount),
        vehicle_compatibility: this.vehicleCompatibility,
        is_car_selected: String(this.isCarSelected),
      });
    },
    table() {
      return {
        isAjax: true,
        url: this.ajaxUrl,
        loadMoreLabel: this.$t('pdp-tab-car-types.more-car-types'),
        tableKey: 'carTypes',
        hasBorder: false,
        showHeaders: false,
        transform: this.sortCarTypesFor('expanded'),
        rowClass: this.rowClass,
      };
    },
    previewTable() {
      const table = {
        ...this.table,
        transform: this.sortCarTypesFor('preview'),
      };

      if (
        this.vehicleCompatibility !== 'SELECTED_CAR_OK' ||
        this.ktypnr === undefined ||
        this.ktypnr === null
      ) {
        return table;
      }

      return {
        ...table,
        url: this.appendQueryParams(this.ajaxUrl, { ktypnr: this.ktypnr }),
      };
    },
    previewCountRequestKey() {
      if (this.vehicleCompatibility !== 'SELECTED_CAR_OK' || !this.ajaxUrl) {
        return '';
      }

      return `${this.ajaxUrl}|${this.ktypnr ?? ''}`;
    },
  },
  watch: {
    // the car selector renders below the portaled car types dialog, so close it to let the user pick a car
    isCarSelectorDialogActive(isActive) {
      if (isActive) {
        this.$refs.section?.hideDialog();
      }
    },
    previewCountRequestKey: {
      immediate: true,
      handler() {
        this.loadCarTypesCount();
      },
    },
  },
  methods: {
    notificationClickTracking(placement) {
      return {
        clickType: 'pdp_car_types_vehicle_notification',
        context: { product_id: this.productId, placement },
      };
    },
    carModel(value = '') {
      let level = 0;

      for (let i = 0; i < value.length; i++) {
        const c = value[i];

        if (c === '(') level++;
        if (c === ')') level--;

        if (
          level === 0 &&
          c === ',' &&
          value[i + 1] === ' ' &&
          /\d/.test(value[i + 2])
        ) {
          return {
            model: value.slice(0, i).trim(),
            specs: value.slice(i + 2).trim(),
          };
        }
      }

      return {
        model: value.trim(),
        specs: '',
      };
    },
    onTableLoaded(payload) {
      if (payload?.tableKey !== 'carTypes') return;
      if (!Number.isFinite(payload?.count)) return;
      if (
        this.vehicleCompatibility === 'SELECTED_CAR_OK' &&
        payload?.loaded === this.previewLimit
      ) {
        return;
      }
      this.carTypesCount = payload.count;
    },
    async loadCarTypesCount() {
      if (this.vehicleCompatibility !== 'SELECTED_CAR_OK' || !this.ajaxUrl) {
        return;
      }

      const { data } = await axios.get(this.ajaxUrl, {
        params: { limit: this.previewLimit },
      });
      const count = data?.carTypes?.count;

      if (Number.isFinite(count)) {
        this.carTypesCount = count;
      }
    },
    interpolateUrl(path, params) {
      const interpolate = this.$options.filters?.interpolate;
      if (typeof interpolate === 'function') return interpolate(path, params);
      return path?.replace('{productId}', this.productId);
    },
    appendQueryParams(path, params = {}) {
      if (!path) return path;

      const [pathAndQuery, hash = ''] = path.split('#');
      const [basePath, query = ''] = pathAndQuery.split('?');
      const searchParams = new URLSearchParams(query);

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.set(key, value);
        }
      });

      const nextQuery = searchParams.toString();

      return `${basePath}${nextQuery ? `?${nextQuery}` : ''}${
        hash ? `#${hash}` : ''
      }`;
    },
    isMatch(entity) {
      if (!entity || entity.carId === undefined || entity.carId === null) {
        return false;
      }

      if (this.ktypnr === undefined || this.ktypnr === null) {
        return false;
      }

      return entity.carId.toString() === this.ktypnr.toString();
    },
    sortCarTypes(entities) {
      if (!Array.isArray(entities) || !this.ktypnr) return entities;

      return [...entities].sort(
        (a, b) => (this.isMatch(b) ? 1 : 0) - (this.isMatch(a) ? 1 : 0)
      );
    },
    sortCarTypesFor(tableName) {
      return (entities) => {
        const sortedEntities = this.sortCarTypes(entities);

        this.isMatchOnTop[tableName] = this.isMatch(sortedEntities?.[0]);

        return sortedEntities;
      };
    },
    rowClass(entity) {
      return this.isMatch(entity) ? 'match-row' : '';
    },
  },
};
</script>

<style scoped lang="scss">
@import './_carTypesTable.scss';

.match-info {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--font-size-SM);
  font-weight: bold;

  .icon {
    color: var(--color-kfzteile24-semantic-green);
  }
}

.brand {
  display: block;
  font-weight: bold;
  line-height: var(--space-3);
}

.type {
  display: block;
  color: var(--color-charcoal-gray);
  line-height: var(--space-3);
}

.pdp-tab-car-types {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);

  .notification {
    margin: 0;
  }

  .section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    ::v-deep {
      @include car-types-table;
    }
  }

  .trigger {
    margin-top: var(--space-1);
    padding: 0;
    color: var(--color-kfzteile24-blue);
    font: inherit;
    font-size: var(--font-size-SM);
    font-weight: normal;
    line-height: var(--space-2--half);
    text-decoration: underline;
    background: transparent;
    border: 0;
    cursor: pointer;
  }
}

.modal-notification {
  margin-bottom: var(--space-2);
}
</style>

<style lang="scss">
@import './_carTypesTable.scss';

.dialog--pdp-car-types {
  @include car-types-table;

  .frame {
    max-width: 1000px;
    max-height: 754px;
    border-radius: 3px;
  }

  .body {
    max-height: calc(754px - 68px);
  }

  @media (max-width: 1032px) {
    .frame {
      max-width: calc(100% - 32px);
    }
  }

  @media (max-height: 794px) {
    .frame {
      max-height: calc(100vh - 40px);
    }

    .body {
      max-height: calc(100vh - 108px);
    }
  }
}
</style>
